import { Controller } from "@hotwired/stimulus"

// Generic variant matrix: rows = dimension_1, columns = dimension_2
// Works with any dimension labels (Color/Size, Flavor/PackSize, etc.)
export default class extends Controller {
  static targets = ["cell", "rowTotal", "colTotal", "grandTotal", "packCount"]
  static values = {
    dimension2Values: Array,
    dimension1Values: Array,
    dimension1Label: { type: String, default: "Variant" },
    dimension2Label: { type: String, default: "Option" },
    readonly: { type: Boolean, default: false },
    distribution: Object
  }

  connect() {
    this.recalculate()
    this.element.setAttribute("role", "grid")
    this.element.setAttribute("aria-label",
      `${this.dimension1LabelValue} by ${this.dimension2LabelValue} quantity matrix`)
  }

  onCellInput() {
    if (this.readonlyValue) return
    this.recalculate()
  }

  // Keyboard navigation: Tab→right, Enter→down, Arrow keys
  onCellKeydown(event) {
    if (this.readonlyValue) return
    const cell = event.currentTarget
    const row = parseInt(cell.dataset.row)
    const col = parseInt(cell.dataset.col)
    const maxRow = (this.dimension1ValuesValue?.length || 1) - 1
    const maxCol = (this.dimension2ValuesValue?.length || 1) - 1
    let nextRow = row, nextCol = col

    switch (event.key) {
      case "Tab":
        event.preventDefault()
        if (event.shiftKey) {
          nextCol = col > 0 ? col - 1 : maxCol
          if (col === 0) nextRow = row > 0 ? row - 1 : maxRow
        } else {
          nextCol = col < maxCol ? col + 1 : 0
          if (col === maxCol) nextRow = row < maxRow ? row + 1 : 0
        }
        break
      case "Enter":
        event.preventDefault()
        nextRow = row < maxRow ? row + 1 : 0
        break
      case "ArrowUp":
        event.preventDefault()
        nextRow = row > 0 ? row - 1 : maxRow
        break
      case "ArrowDown":
        event.preventDefault()
        nextRow = row < maxRow ? row + 1 : 0
        break
      case "ArrowLeft":
        event.preventDefault()
        nextCol = col > 0 ? col - 1 : maxCol
        break
      case "ArrowRight":
        event.preventDefault()
        nextCol = col < maxCol ? col + 1 : 0
        break
      default:
        return
    }

    const nextCell = this.element.querySelector(`[data-row="${nextRow}"][data-col="${nextCol}"]`)
    if (nextCell) {
      nextCell.focus()
      nextCell.select()
    }
  }

  applyPackTemplate() {
    if (!this.hasPackCountTarget || !this.distributionValue) return
    const packCount = parseInt(this.packCountTarget.value) || 0
    const dist = this.distributionValue

    this.dimension1Values.forEach((d1, rowIdx) => {
      this.dimension2ValuesValue.forEach((d2, colIdx) => {
        const cell = this.element.querySelector(`[data-row="${rowIdx}"][data-col="${colIdx}"]`)
        if (cell && dist[d2] !== undefined) {
          cell.value = dist[d2] * packCount
        }
      })
    })
    this.recalculate()
  }

  recalculate() {
    const rows = this.dimension1ValuesValue?.length || 0
    const cols = this.dimension2ValuesValue?.length || 0
    let grandTotal = 0

    for (let r = 0; r < rows; r++) {
      let rowSum = 0
      for (let c = 0; c < cols; c++) {
        const cell = this.element.querySelector(`[data-row="${r}"][data-col="${c}"]`)
        if (cell) rowSum += parseInt(cell.value) || 0
      }
      const rowEl = this.element.querySelector(`[data-row-total="${r}"]`)
      if (rowEl) rowEl.textContent = rowSum
      grandTotal += rowSum
    }

    for (let c = 0; c < cols; c++) {
      let colSum = 0
      for (let r = 0; r < rows; r++) {
        const cell = this.element.querySelector(`[data-row="${r}"][data-col="${c}"]`)
        if (cell) colSum += parseInt(cell.value) || 0
      }
      const colEl = this.element.querySelector(`[data-col-total="${c}"]`)
      if (colEl) colEl.textContent = colSum
    }

    const gtEl = this.element.querySelector("[data-grand-total]")
    if (gtEl) gtEl.textContent = grandTotal
  }

  get dimension1Values() {
    return this.dimension1ValuesValue || []
  }
}
